import { action } from '@ember/object';
import Component from '@glimmer/component';
import { startOfDay, endOfDay, subDays, subYears, formatISO } from 'date-fns';
import PowerSelect from 'ember-power-select/components/power-select';

const DEFAULT_FILTER = 'All Time';

type DateRangeOption =
  | 'Today'
  | 'Yesterday'
  | 'Last 7 days'
  | 'Last 30 days'
  | 'Last 90 days'
  | 'Past year'
  | 'All Time';

interface DateRangeSelectSignature {
  Args: {
    /** Callback function to set the date range */
    setDateRange: (option: DateRangeOption, start: string, end: string) => void;
    /** Currently selected date range filter */
    dateRangeFilter?: DateRangeOption;
  };
  Element: HTMLDivElement;
}

/**
 * Date range selection component for visitor log filtering
 */
export default class DateRangeSelect extends Component<DateRangeSelectSignature> {
  get dateRange(): DateRangeOption {
    return this.args.dateRangeFilter ?? DEFAULT_FILTER;
  }

  get isDefaultFilter(): boolean {
    return this.dateRange === DEFAULT_FILTER;
  }

  dateRangeFilterOptions: readonly DateRangeOption[] = [
    'Today',
    'Yesterday',
    'Last 7 days',
    'Last 30 days',
    'Last 90 days',
    'Past year',
    'All Time',
  ] as const;

  @action
  selectDateRange(option: DateRangeOption): void {
    if (option === 'All Time') {
      this.args.setDateRange(option, '', '');
      return;
    }

    let start = new Date();
    let end = new Date();

    switch (option) {
      case 'Yesterday':
        start = subDays(new Date(), 1);
        end = subDays(new Date(), 1);
        break;
      case 'Last 7 days':
        start = subDays(new Date(), 7);
        break;
      case 'Last 30 days':
        start = subDays(new Date(), 30);
        break;
      case 'Last 90 days':
        start = subDays(new Date(), 90);
        break;
      case 'Past year':
        start = subYears(new Date(), 1);
        break;
      case 'Today':
      default:
        // Today is the default case - no modification needed
        break;
    }

    const startFormatted = formatISO(startOfDay(start));
    const endFormatted = formatISO(endOfDay(end));

    this.args.setDateRange(option, startFormatted, endFormatted);
  }

  <template>
    <div ...attributes data-test-visitor-log-visitor-log-table-date-range-select>
      <div data-test-date-range-select>
        <PowerSelect
          @selected={{this.dateRange}}
          @searchEnabled={{false}}
          @triggerClass={{unless this.isDefaultFilter "not-default-filter-selected"}}
          @options={{this.dateRangeFilterOptions}}
          @onChange={{this.selectDateRange}}
          as |option|
        >
          <span data-test-select-option>{{option}}</span>
        </PowerSelect>
      </div>
    </div>
  </template>
}
